import { useTranslation } from 'react-i18next'
import { BookOpen, ExternalLink } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import type { CitationResponse } from '@/api/types'

interface CitationSheetProps {
  citation: CitationResponse | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CitationSheet({ citation, open, onOpenChange }: CitationSheetProps) {
  const { t } = useTranslation()

  if (!citation) return null

  // Determine if this is a non-document source (medication, terminology)
  const isNonDocumentSource =
    citation.source_type === 'medication' ||
    citation.source_type === 'terminology' ||
    citation.source_type === 'web' ||
    citation.document?.startsWith('SNOMED:') ||
    citation.document?.startsWith('SAI:')

  // Check if page numbers are meaningful (not 1-1 default)
  const hasRealPageNumbers = citation.page_start > 0 &&
    !(citation.page_start === 1 && citation.page_end === 1)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-citation-700">
            <BookOpen className="w-5 h-5" />
            {citation.document}
          </SheetTitle>
          {!isNonDocumentSource && hasRealPageNumbers && (
            <SheetDescription>
              {t('source.pageRange', { start: citation.page_start, end: citation.page_end })}
            </SheetDescription>
          )}
        </SheetHeader>
        <div className="mt-4 overflow-y-auto">
          <div className="bg-citation-50 border border-citation-200 rounded-lg p-4">
            <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">
              {citation.cited_text}
            </p>
          </div>
          {citation.url && (
            <a
              href={citation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {t('source.viewOriginal')}
            </a>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
